import type { FC, ReactNode } from 'react'
import type { GetServerSideProps } from 'next'
import type { Product, ProductType } from '@/types'

import Link from 'next/link'
import { useRouter } from 'next/router'
import { useEffect, useMemo, useState } from 'react'
import { Feather, PackageCheck, Ruler } from 'lucide-react'
import { cn } from '@/lib/utils'
import { getProducts } from '@/lib/products'
import { Layout } from '@/components/generals/layout'
import { Seo } from '@/components/generals/seo'
import PageIntro from '@/components/generals/page-intro'
import Reveal from '@/components/generals/reveal'
import ProductCard from '@/components/shop/product-card'
import { PRODUCT_TYPES } from '@/constants/content'

interface ShopProps {
    products: Array<Product>
    initialType: ProductType | null
    failed: boolean
}

const parseType = (value: unknown): ProductType | null =>
    PRODUCT_TYPES.some(entry => entry.value === value) ? value as ProductType : null

const PROMISES = [
    { icon: Feather, title: 'Made by hand', text: 'No two sheets are quite alike. Small variations in tone and weight are the mark of the vat, not a flaw.' },
    { icon: PackageCheck, title: 'Shipped worldwide', text: 'Sent flat between boards with tracking from Samarkand. Most parcels arrive in 7 to 14 days.' },
    { icon: Ruler, title: 'Custom sizes', text: 'Need a size, weight or colour we do not list? We make paper to order for studios and conservators.' }
]

const Shop: FC<ShopProps> = ({ products, initialType, failed }): ReactNode => {
    const router = useRouter()
    const [filter, setFilter] = useState<ProductType | null>(initialType)

    // Keeps the filter in step with the address bar, including the back button
    useEffect(() => {
        if (!router.isReady) return
        setFilter(parseType(router.query.type))
    }, [router.isReady, router.query.type])

    const choose = (type: ProductType | null): void => {
        setFilter(type)
        router.replace({ pathname: '/shop', query: type ? { type } : {} }, undefined, { shallow: true, scroll: false })
    }

    const visible = useMemo(() => (filter ? products.filter(product => product.type === filter) : products), [products, filter])

    const filters: Array<{ value: ProductType | null; label: string; count: number }> = [
        { value: null, label: 'All paper', count: products.length },
        ...PRODUCT_TYPES.map(entry => ({
            value: entry.value,
            label: entry.label,
            count: products.filter(product => product.type === entry.value).length
        }))
    ]

    return (
        <Layout>
            <Seo
                title='Shop'
                description='Buy handmade Samarkand silk paper: polished sheets in three sizes, hand-bound notebooks and wedding stationery dyed with indigo and pomegranate. Shipped worldwide from Uzbekistan.'
            />

            <PageIntro
                id='shop-intro'
                eyebrow='The paper shop'
                title='Sheets, books and wedding paper'
                intro='Everything here was formed at our vat, dried on our wall and polished, sewn or dyed in our courtyard. Prices are in US dollars, before shipping.'
            />

            <section id='products' aria-label='Products' className='container-page pb-20 md:pb-28'>
                <div className='sticky top-[72px] z-30 -mx-5 mb-12 border-y border-bark/10 bg-paper/95 px-5 py-3 sm:-mx-8 sm:px-8 md:top-20 lg:mx-0 lg:px-0'>
                    <div role='group' aria-label='Filter by type' className='no-scrollbar flex gap-2 overflow-x-auto'>
                        {filters.map(entry => {
                            const pressed = filter === entry.value
                            return (
                                <button
                                    key={entry.label}
                                    type='button'
                                    aria-pressed={pressed}
                                    onClick={() => choose(entry.value)}
                                    className={cn(
                                        'inline-flex shrink-0 items-center gap-2 rounded-full border px-4 py-2 text-sm transition-colors duration-300',
                                        'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-bark',
                                        pressed ? 'border-bark bg-bark text-paper' : 'border-bark/20 text-bark-soft hover:border-bark/50 hover:text-bark'
                                    )}
                                >
                                    {entry.label}
                                    <span className={cn('text-xs tabular-nums', pressed ? 'text-paper/75' : 'text-bark-soft')}>{entry.count}</span>
                                </button>
                            )
                        })}
                    </div>
                </div>

                <p aria-live='polite' className='sr-only'>
                    {visible.length} {visible.length === 1 ? 'product' : 'products'} shown
                </p>

                {failed ? (
                    <div className='rounded-[3px] border border-dashed border-bark/25 px-6 py-16 text-center'>
                        <p className='font-heading text-2xl text-bark'>The shop could not be opened just now.</p>
                        <p className='mt-3 text-bark-soft'>Please refresh the page in a moment.</p>
                    </div>
                ) : visible.length === 0 ? (
                    <div className='rounded-[3px] border border-dashed border-bark/25 px-6 py-16 text-center'>
                        <p className='font-heading text-2xl text-bark'>Nothing on the drying wall here yet.</p>
                        <p className='mt-3 text-bark-soft'>New batches are on their way. Try another type, or write to us for a custom order.</p>
                        <Link href='/contact' className='mt-6 inline-block font-medium text-pomegranate'>
                            <span className='ink-link pb-0.5'>Ask about a custom order</span>
                        </Link>
                    </div>
                ) : (
                    <ul className='grid grid-cols-1 gap-x-6 gap-y-16 min-[480px]:grid-cols-2 sm:gap-x-8 lg:grid-cols-3 lg:gap-x-12 lg:gap-y-20 lg:[&>li:nth-child(3n+2)]:mt-20'>
                        {visible.map((product, index) => (
                            <li key={product.id}>
                                <Reveal delay={(index % 3) * 120}>
                                    <ProductCard
                                        product={product}
                                        sizes='(min-width: 1024px) 30vw, (min-width: 480px) 46vw, 92vw'
                                        priority={index < 3}
                                        showTagline
                                        headingLevel='h2'
                                    />
                                </Reveal>
                            </li>
                        ))}
                    </ul>
                )}
            </section>

            <section id='shop-promises' aria-label='How we make and ship' className='border-t border-bark/10 bg-paper-deep/50'>
                <ul className='container-page grid gap-10 py-16 md:grid-cols-3 md:py-20'>
                    {PROMISES.map(({ icon: Icon, title, text }) => (
                        <li key={title} className='flex gap-5'>
                            <Icon aria-hidden='true' className='mt-1 size-6 shrink-0 text-bark-soft' strokeWidth={1.5} />
                            <div>
                                <p className='font-heading text-[1.3rem] text-bark'>{title}</p>
                                <p className='mt-2 text-[0.98rem] leading-relaxed text-bark-soft'>{text}</p>
                            </div>
                        </li>
                    ))}
                </ul>
            </section>
        </Layout>
    )
}

export const getServerSideProps: GetServerSideProps<ShopProps> = async ({ query, res }) => {
    res.setHeader('Cache-Control', 'public, s-maxage=60, stale-while-revalidate=300')
    const initialType = parseType(query.type)

    try {
        const products = await getProducts()
        return { props: { products, initialType, failed: false } }
    } catch (error) {
        console.error('Products could not be loaded', error instanceof Error ? error.message : error)
        return { props: { products: [], initialType, failed: true } }
    }
}

export default Shop
